import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  Check,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  Flame,
  ShieldAlert,
  Radio,
  Phone,
  Cable,
  MessageCircle,
  Network,
  PencilLine,
  Plug,
  Plus,
  Scissors,
  Trash2,
  Wand2,
  X,
  type LucideIcon,
} from "lucide-react";
import clsx from "clsx";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import type { AnalyticsEventProperties } from "utils/analytics";
import type { CableBoqItem, CableSystem } from "../../utils/fetchCables";
import { systemMeta, isSpecialSystem } from "../cableSystems";
import { formatMetres as formatMetresFor } from "../formatMetres";
import type { EditableCableTakeoff } from "../useEditableCableTakeoff";
import type { CableDestinationInfo } from "../useCableDestinationInfo";
import type { RouteStatusKind } from "../karlRoutes";
import { parseDestIndex } from "../cableEditGeometry";
import { DEFAULT_SPLIT_SIZE, FALLBACK_SPLIT_SIZE } from "../splitCircuit";
import { lumpedCircuitLimit } from "../lumpedCircuitLimit";
import type { AutoSplitPlan, SwitchChoice } from "../splitByRooms";
import { AutoSplitPreview } from "./AutoSplitPreview";
import { type CircuitOption } from "./types";
import { trayShare } from "./trayShare";
import { PanelSelect } from "../../PanelSelect";
import { DestinationList } from "./DestinationList";
import { WiringBasis } from "./WiringBasis";
import { DiagramPageLink } from "./DiagramPageLink";
import type { KarlDiagramControl } from "../CablesChatPanel/types";
import { wiringBasisOf } from "../wiringBasisOf";
import { switchingRoleOf } from "../switchingRoleOf";
import { cableTypesOfCircuit } from "../circuitLegs";
import type { DestinationEntry } from "./groupDestinations";

type AutoSplitDraft =
  | { phase: "closed" }
  | { phase: "open"; size: string; switches: SwitchChoice };

interface Pt {
  x: number;
  y: number;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/** A distinct lucide glyph per special installation system (badge + board prefix). */
const SYSTEM_ICON: Partial<Record<CableSystem, LucideIcon>> = {
  bma: Flame,
  knx: Network,
  media: Radio,
  intercom: Phone,
  safety: ShieldAlert,
  earthing: Cable,
};

/** A board the feed point can be switched to. `label` is the marker token the
 * geometry keys the board by; `name` is what the estimator reads. */
export interface FeedPointOption {
  label: string;
  name: string;
}

// Rendered only while an override is in force, so its presence is itself part
// of the mark that the value on the row is the estimator's.
const FactReset = ({
  fieldLabel,
  onReset,
}: {
  fieldLabel: string;
  onReset: () => void;
}) => {
  const { t } = useTranslation();
  const name = `${fieldLabel} · ${t("smartSurveyor.cablesBeta.restore")}`;
  return (
    <button
      type="button"
      aria-label={name}
      title={name}
      onClick={(e) => {
        e.stopPropagation();
        onReset();
      }}
      className="shrink-0 text-blue-700 hover:text-blue-900"
    >
      <RotateCcw size={14} />
    </button>
  );
};

const factFieldClass = (overridden: boolean) =>
  clsx(
    "max-w-[9.5rem] truncate rounded border bg-white px-1 py-0.5 text-[13px] focus:border-blue-400 focus:outline-none",
    overridden
      ? "border-blue-300 font-medium text-blue-700"
      : "border-slate-300 text-slate-700",
  );

const FeedPointField = ({
  value,
  options,
  overridden,
  onPick,
  onReset,
}: {
  value: string;
  options: FeedPointOption[];
  overridden: boolean;
  onPick: (sourceLabel: string) => void;
  onReset: () => void;
}) => {
  const { t } = useTranslation();
  const fieldLabel = t("smartSurveyor.cablesBeta.factSource");
  // The board in force may be one the run named and this plan's roster does not
  // list; it still has to be readable, so it is offered as its own choice.
  const choices =
    value && !options.some((o) => o.label === value)
      ? [{ label: value, name: value }, ...options]
      : options;
  // No `color`: a board has none on the plan, and PanelSelect draws no dot for
  // a list where nothing carries one.
  const boardOptions = choices.map((o) => ({
    value: o.label,
    label: o.name || o.label,
  }));
  return (
    <span className="inline-flex min-w-0 items-center justify-end gap-1">
      <span className="min-w-0 max-w-[11rem] flex-1">
        <PanelSelect
          value={value}
          options={boardOptions}
          placeholder={t("smartSurveyor.cablesBeta.factSourcePick")}
          ariaLabel={fieldLabel}
          isEdited={overridden}
          onPick={onPick}
        />
      </span>
      {overridden && <FactReset fieldLabel={fieldLabel} onReset={onReset} />}
    </span>
  );
};

// One selector for the type, like the board: he picks a type the plan knows, or makes one by typing it.
const CableTypeField = ({
  value,
  options,
  overridden,
  onPick,
  onCreate,
  onReset,
}: {
  value: string;
  options: string[];
  overridden: boolean;
  onPick: (cableType: string) => void;
  onCreate: (cableType: string) => void;
  onReset: () => void;
}) => {
  const { t } = useTranslation();
  const fieldLabel = t("smartSurveyor.cablesBeta.factCableType");
  const choices =
    value && !options.includes(value) ? [value, ...options] : options;
  return (
    <span className="inline-flex min-w-0 items-center justify-end gap-1">
      <span className="min-w-0 max-w-[11rem] flex-1">
        <PanelSelect
          value={value}
          options={choices.map((type) => ({ value: type, label: type }))}
          placeholder={t("smartSurveyor.cablesBeta.cableTypePick")}
          ariaLabel={fieldLabel}
          isEdited={overridden}
          onPick={onPick}
          onCreate={onCreate}
          createLabel={(text) =>
            t("smartSurveyor.cablesBeta.cableTypesCreate", { name: text })
          }
        />
      </span>
      {overridden && <FactReset fieldLabel={fieldLabel} onReset={onReset} />}
    </span>
  );
};

const CircuitTextField = ({
  fieldLabel,
  value,
  overridden,
  onCommit,
  isTaken,
}: {
  fieldLabel: string;
  value: string;
  overridden: boolean;
  onCommit: (text: string) => void;
  /** A value another circuit already carries is refused, never written. */
  isTaken?: (text: string) => boolean;
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState(value);
  const isRefused = Boolean(
    isTaken && text.trim() && text.trim() !== value && isTaken(text.trim()),
  );

  // An emptied field gives the circuit its generated name back.
  const commit = (next: string) => {
    if (isRefused) return;
    if (next.trim() !== value) onCommit(next.trim());
  };

  useEffect(() => setText(value), [value]);

  return (
    <span className="inline-flex flex-col items-end gap-0.5">
      <span className="inline-flex items-center justify-end gap-1">
        <input
          type="text"
          aria-label={fieldLabel}
          aria-invalid={isRefused || undefined}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => (isRefused ? setText(value) : commit(text))}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit(text);
            if (e.key === "Escape") setText(value);
          }}
          onClick={(e) => e.stopPropagation()}
          className={clsx(
            factFieldClass(overridden),
            isRefused && "border-red-500 text-red-700",
          )}
        />
        {overridden && (
          <FactReset fieldLabel={fieldLabel} onReset={() => onCommit("")} />
        )}
      </span>
      {isRefused && (
        <span className="text-[12px] font-medium text-red-700">
          {t("smartSurveyor.cablesBeta.runAddKeyTaken")}
        </span>
      )}
    </span>
  );
};

const UnderReviewMark = () => {
  const { t } = useTranslation();
  return (
    <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-blue-100 px-2 py-0.5 text-[13px] font-semibold text-blue-700">
      <span className="karl-dot inline-block h-1.5 w-1.5 rounded-full bg-blue-600" />
      {t("smartSurveyor.cablesBeta.routeMarkUnderReview")}
    </span>
  );
};

const SettledMark = ({ status }: { status: RouteStatusKind }) => {
  const { t } = useTranslation();
  if (status === "untouched") return null;
  const confirmed = status === "confirmed";
  // The tint says he settled the run; the glyph says which way, and the words
  // he used to read are what it is called to a screen reader and on hover.
  const name = t(
    confirmed
      ? "smartSurveyor.cablesBeta.routeMarkConfirmed"
      : "smartSurveyor.cablesBeta.routeMarkCorrected",
  );
  const Glyph = confirmed ? Check : PencilLine;
  return (
    // A span, never a button: this reports what he settled with Karl, it does
    // not offer to settle it.
    <span
      role="img"
      aria-label={name}
      title={name}
      className="inline-flex shrink-0 items-center text-emerald-700"
    >
      <Glyph size={14} />
    </span>
  );
};

export interface CircuitCardProps {
  item: CableBoqItem;
  selected: boolean;
  /** True while Karl's question is about this run. Marked apart from a
   * selection: he needs to tell "the one being asked about" from "the one I
   * clicked" without reading either. */
  underReview?: boolean;
  onSelect?: (circuitId: string | null) => void;
  onHover?: (circuitId: string | null) => void;
  edit?: EditableCableTakeoff;
  destinations: CableDestinationInfo[];
  /** Other circuits a device can be moved into (for the "Move to route" menu). */
  circuitOptions?: CircuitOption[];
  /** Highlight (blink) a device on the plan while hovering its destination row. */
  onHighlightDestination?: (pos: Pt | null) => void;
  onShowHeightMarks?: (
    marks: { x: number; y: number; kind: "rise" | "drop" }[],
  ) => void;
  /** What the estimator has settled about this route while walking it with Karl. */
  status?: RouteStatusKind;
  /** True while Karl is waiting to be told which run some devices belong on. */
  isPickingRun?: boolean;
  onPickRun?: (circuitId: string) => void;
  /** Puts this run to Karl in the thread. */
  onAskKarlAbout?: (circuitId: string) => void;
  /** Opens the assignment mode on this circuit alone, to split it by hand. */
  onSplit?: (circuitId: string) => void;
  /** What "Automatisch aufteilen" would make of this circuit at this size: by room where the plan has rooms. */
  planAutoSplit?: (
    circuitId: string,
    maxPerCircuit: number,
    takeSwitches: boolean,
  ) => AutoSplitPlan | null;
  /** Takes the plan he was shown, as one step. */
  onAutoSplit?: (circuitId: string, plan: AutoSplitPlan) => void;
  /** Where the opened card shows its details, beside the list; absent keeps them under the row. */
  detailHost?: HTMLElement | null;
  /** Locale-aware metre formatting, so a German reads 6.006,5 not 6,006.5. */
  formatMetres?: (value: number) => string;
  /** Boards available on this plan, offered as the feed point's suggested list. */
  feedPointOptions?: FeedPointOption[];
  /** Cable types already used in this project, offered as suggestions. */
  cableTypeOptions?: string[];
  /** The id printed beside this run's devices — "A31". `null` says the run
   * genuinely carries none; absent says nothing here knows. Read through
   * `readCircuitKey`, so the row and Karl's card name a run the same way. */
  circuitKey?: string | null;
  /** The schematic's own designator for the breaker feeding this run — "-0F1".
   * Absent unless the schedule named it and the name differs from the printed
   * id, which is exactly when it is worth the room. */
  circuitBreaker?: string;
  /** True for a run the estimator created that the estimation never published. */
  isCreatedByYou?: boolean;
  /**
   * Drops a run he created. `toCircuitId` null leaves its devices where the
   * estimation had them — the choice is his, and it is asked before the run
   * goes, never after.
   */
  onRemoveCircuit?: (circuitId: string, toCircuitId: string | null) => void;
  /** Finds this run on the Verteilerplan; absent = no schematic loaded. */
  diagram?: KarlDiagramControl;
  /** True when another circuit on this plan already carries that identifier. */
  isKeyTaken?: (key: string) => boolean;
}

export function CircuitCard({
  item,
  selected,
  underReview = false,
  onSelect,
  onHover,
  edit,
  destinations,
  circuitOptions = [],
  onHighlightDestination,
  onShowHeightMarks,
  status = "untouched",
  isPickingRun = false,
  onPickRun,
  onAskKarlAbout,
  onSplit,
  onAutoSplit,
  planAutoSplit,
  detailHost,
  formatMetres,
  feedPointOptions = [],
  cableTypeOptions = [],
  circuitKey,
  circuitBreaker,
  isCreatedByYou = false,
  onRemoveCircuit,
  diagram,
  isKeyTaken,
}: CircuitCardProps) {
  const { t, i18n } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [autoSplit, setAutoSplit] = useState<AutoSplitDraft>({
    phase: "closed",
  });
  // Never a bare toLocaleString(): with no locale it follows the BROWSER, so a
  // German session on an en-US Chrome prints 6,006 m for six thousand metres.
  const num =
    formatMetres ??
    ((value: number) => formatMetresFor({ value, language: i18n.language }));
  const cardRef = useRef<HTMLLIElement>(null);

  // Accordion: a card is expanded ONLY when it's the selected route — so you
  // only ever traverse the devices of the route that's currently shown on the
  // plan (no interacting with other, hidden circuits' devices).
  const open = selected;

  // Plan → card sync: scroll the selected card into view. Centred while Karl
  // is walking, so the run he is asking about is not left against an edge with
  // its neighbours out of sight.
  useEffect(() => {
    if (!selected) return;
    cardRef.current?.scrollIntoView({
      block: underReview ? "center" : "nearest",
      behavior: underReview ? "smooth" : "auto",
    });
  }, [selected, underReview]);

  // Selection uses circuit || text (matches the plan's circuit id); the editable
  // length breakdown is only available when a real circuit id exists.
  const selectId = item.circuit || item.text;
  const circuitId = item.circuit;
  const canEditLength = !!edit?.derivedGeometry && !!circuitId;

  const derivedLen =
    edit?.derivedGeometry && circuitId
      ? edit.derivedLengthsByCircuit[circuitId]
      : undefined;
  // Its board was removed, so it is fed from nowhere. Kept in the list rather
  // than dropped: the devices are still drawn and still have to go somewhere,
  // and a circuit that silently vanishes is a total nobody can reconcile.
  const isOrphaned = Boolean(
    circuitId && edit?.orphanedCircuitIds.includes(circuitId),
  );
  const qty = derivedLen !== undefined ? round1(derivedLen) : item.quantity;
  // Both halves off the run's OWN circuit record, never the BoQ row: the BoQ
  // item carries nothing tray-shaped, and pairing the tray metres with a length
  // his edits have moved would compare two different things.
  const trayUsage =
    circuitId !== undefined
      ? edit?.derivedGeometry?.trayByCircuit?.[circuitId]
      : undefined;
  const share = trayShare({
    onTrayM: trayUsage?.onTrayM,
    routedM: trayUsage?.billedM,
  });
  // The router prefers going ROUND a wall; a hole means round cost more than it
  // was worth. Nobody's metres record that decision, so the count is the only
  // place it is visible — and drilling two walls is not a detail to leave off.
  const holes =
    circuitId !== undefined
      ? (edit?.wallCrossingsByCircuit?.[circuitId] ?? 0)
      : 0;

  const measured =
    circuitId !== undefined ? (edit?.measuredByCircuit[circuitId] ?? 0) : 0;
  const billed =
    circuitId !== undefined
      ? (edit?.billedByCircuit[circuitId] ?? measured)
      : measured;
  const vertical =
    circuitId !== undefined ? edit?.verticalByCircuit[circuitId] : undefined;
  const total = derivedLen !== undefined ? derivedLen : qty;

  // The run's own facts for this circuit, and the values actually in force. Read
  // straight off `edit` on every render: changing the feed point remeasures the
  // circuit, so anything cached here would show metres from the old board.
  const canEditFacts = !!edit && !!circuitId;
  const runCableType = item.cable_type ?? "";
  const runFeedPoint =
    feedPointOptions.find(
      (o) => o.label === item.board || o.name === item.board,
    )?.label ??
    item.board ??
    "";
  const cableType =
    (circuitId !== undefined
      ? edit?.cableTypeByCircuit?.[circuitId]
      : undefined) ?? runCableType;
  const handSetFeedPoint =
    circuitId !== undefined ? edit?.feedPointByCircuit?.[circuitId] : undefined;
  // The board in force, zones included — not only one he set by hand.
  const feedPoint =
    handSetFeedPoint ??
    (circuitId !== undefined
      ? edit?.feedingBoardByCircuit?.[circuitId]
      : undefined) ??
    runFeedPoint;
  const heightBoardName =
    feedPointOptions.find((option) => option.label === feedPoint)?.name ??
    feedPoint;
  const cableTypeOverridden = !!cableType && cableType !== runCableType;
  const feedPointOverridden =
    !!handSetFeedPoint && handSetFeedPoint !== runFeedPoint;

  // destinationInfo entries are ordered to match derivedGeometry.byCircuit[cid].paths,
  // so path[i].destinationId is the id for the i-th destination shown in this card.
  const paths =
    circuitId !== undefined
      ? edit?.derivedGeometry?.byCircuit[circuitId]?.paths
      : undefined;
  const deviceTypes = edit?.derivedGeometry?.deviceTypeByElementId;
  const wiringBasis = paths?.length
    ? wiringBasisOf({
        paths,
        roleOf: (path) =>
          switchingRoleOf({
            deviceType: path.elementId
              ? deviceTypes?.[path.elementId]
              : undefined,
            name: path.destinationName,
          }),
        nameOf: (path) =>
          (path.elementId ? deviceTypes?.[path.elementId]?.name : undefined) ??
          path.destinationName,
      })
    : null;
  const legMetres = circuitId ? edit?.legsByCircuit?.[circuitId] : undefined;
  const legTypes =
    (circuitId ? edit?.cableTypeByLegByCircuit?.[circuitId] : undefined) ?? {};
  const typeShares = legMetres
    ? cableTypesOfCircuit({ legs: legMetres, circuitType: cableType, legTypes })
    : [];
  const destIdFor = (i: number): string | undefined =>
    paths?.[i]?.destinationId;
  const posFor = (i: number): Pt | undefined => {
    const wps = paths?.[i]?.waypoints;
    return wps && wps.length ? wps[wps.length - 1] : undefined;
  };

  // The run's OWN number, off the destination id, never the render index: the
  // list is grouped and a device struck from the middle would otherwise renumber
  // everything below it — the number has to name the same detector all day.
  const destinationEntries: DestinationEntry[] = destinations.map((info, i) => {
    const destinationId = destIdFor(i);
    return {
      info,
      destinationId,
      number:
        destinationId === undefined
          ? undefined
          : parseDestIndex(destinationId) + 1,
      pos: posFor(i),
    };
  });

  // Devices removed from THIS circuit — dropped from geometry, shown so the user
  // can restore them.
  const removedForCircuit =
    circuitId !== undefined
      ? (edit?.removedDeviceMarkers ?? []).filter(
          (m) => m.circuitId === circuitId,
        )
      : [];

  // What the run actually CARRIES, off the geometry — never the resolved
  // destination list. That list is a display join and can be empty while the
  // cable is drawn, and a delete that read it would strike a run out from under
  // eleven devices without asking where they go.
  const deviceCount = paths?.length ?? destinations.length;
  // Worked out as he types the size, so what he takes is exactly what he was shown.
  const autoSplitPlan =
    autoSplit.phase === "open" &&
    circuitId &&
    planAutoSplit &&
    Number(autoSplit.size) >= 1
      ? planAutoSplit(
          circuitId,
          Math.floor(Number(autoSplit.size)),
          autoSplit.switches === "take",
        )
      : null;
  const categoryOfThis = circuitId
    ? edit?.categoryByCircuit?.[circuitId]
    : undefined;
  const lumpedLimit = lumpedCircuitLimit({ category: categoryOfThis });
  const isLumped = deviceCount > lumpedLimit;

  const rgb = paths?.[0]?.rgb;
  const swatch = rgb ? `rgb(${rgb[0]},${rgb[1]},${rgb[2]})` : "#94a3b8";

  // Installation system (agent contract). Absent / "power" = normal, unchanged.
  const special = isSpecialSystem(item.system);
  const sys = systemMeta(item.system);
  const SysIcon = item.system
    ? SYSTEM_ICON[item.system as CableSystem]
    : undefined;
  const sysLabel = t(sys.labelKey);
  // The Kurztext of the row: what this run is made of, or the board it hangs on
  // when the run carries no cable type. Unchanged from what the row always said.
  const kurztext = item.cable_type || item.board;
  // The id he scans 83 rows for gets a column of its own, in front of the name,
  // so it is always in the same place and never buried mid-string.
  const hasKeyColumn = circuitKey !== undefined || !!circuitBreaker;
  // The PRINTED id leads: it is the one annotated beside the devices on the
  // sheet in front of him, and the one Karl's questions name. A breaker fills
  // the column only when there is no printed id for it to displace.
  const leadId = circuitKey || circuitBreaker;
  // Karl's own sentence for a run he could only group by symbol — the row must
  // not invent a second way of saying it.
  const noPrintedIdName = t("smartSurveyor.cablesBeta.askRouteWhySymbol");
  // With no id printed, the BoQ name is all the identity this run has, so it
  // keeps the lead rather than being demoted under an empty column.
  const nameLeads = !leadId;
  const showName = !!item.text && item.text !== leadId;
  // The schematic's designator for the breaker feeding this run — a second name
  // for the same way, so it rides under the id rather than beside it. Never
  // repeated when it is already the lead.
  const breakerBelow =
    circuitBreaker && circuitBreaker !== leadId ? circuitBreaker : null;
  // Karl's blue marker and the pick affordance each own the row while they are
  // up; a second colour under them is one state too many to read at once.
  const showSettled = !isPickingRun && !underReview && status !== "untouched";
  const toggleSelect = () => {
    if (!selected && selectId) {
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUP_SELECTED, {
        groupKind: "cableTakeoff",
        groupTypeId: selectId,
        elementCount: deviceCount,
      });
    }
    onSelect?.(selected ? null : selectId || null);
  };
  const trackRunEdited = (
    field: AnalyticsEventProperties<"Smart Surveyor Cable Run Edited">["field"],
  ) => trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_RUN_EDITED, { field });
  const trackRunDeleted = (
    deviceHandling: "none" | "movedToRun" | "restored",
  ) => {
    if (!circuitId) return;
    trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUP_DELETED, {
      groupKind: "cableTakeoff",
      groupTypeId: circuitId,
      elementCount: deviceCount,
      deviceHandling,
    });
  };

  // Opened beside the list, the row stays put and the details get the room; a header says which run they are.
  const placeDetails = (details: ReactNode) => {
    if (!detailHost) return details;
    return createPortal(
      <section data-testid="circuit-detail" aria-label={leadId ?? item.text}>
        <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-blue-100 bg-blue-50 px-3 py-2">
          <span className="min-w-0 flex-1 truncate text-[16px] font-semibold text-blue-800">
            {leadId ?? item.text}
            {leadId && item.text && item.text !== leadId && (
              <span className="ml-2 text-[13px] font-normal text-slate-600">
                {item.text}
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={toggleSelect}
            aria-label={t("smartSurveyor.cablesBeta.detailClose")}
            title={t("smartSurveyor.cablesBeta.detailClose")}
            className="grid h-8 w-8 flex-none place-items-center rounded-md text-slate-500 hover:bg-white hover:text-slate-800"
          >
            <X size={16} />
          </button>
        </div>
        {details}
      </section>,
      detailHost,
    );
  };

  // Open beside the list, Esc closes it — unless he is typing in one of its fields.
  useEffect(() => {
    if (!open || !detailHost) return;
    const onKey = (event: KeyboardEvent) => {
      const isTyping =
        event.target instanceof Element &&
        Boolean(event.target.closest("input, textarea, [role=listbox]"));
      if (event.key !== "Escape" || isTyping) return;
      onSelect?.(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, detailHost, onSelect]);

  return (
    <li
      ref={cardRef}
      className={clsx(
        "list-none",
        // A special-system ring reads as a DIFFERENT installation (e.g. BMA = red)
        // via a left accent border. Blue selection border takes precedence.
        special && !selected && ["rounded-l border-l-4", sys.accentBorder],
        selected && "my-1 overflow-hidden rounded-lg border-2 border-blue-400",
        // Keyed by the circuit so the ring pulses its three times per question,
        // not once for the whole walk.
        underReview && "karl-await rounded-lg",
        isPickingRun &&
          "rounded-lg ring-1 ring-inset ring-blue-200 hover:bg-blue-50/70 hover:ring-2 hover:ring-blue-400",
        isOrphaned && "opacity-50 grayscale",
      )}
    >
      {/* row — read as an LV line: Kennung · Kurztext · Menge · Einheit. No
          Pos. column: see KARL.md. Click to isolate on plan. */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggleSelect}
        onKeyDown={(e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault();
          toggleSelect();
        }}
        onMouseEnter={() => onHover?.(selectId || null)}
        onMouseLeave={() => onHover?.(null)}
        className={clsx(
          "flex w-full items-center gap-2.5 px-2 py-2 text-left transition-colors",
          // The tint is at rest, not only on hover: what he has settled has to
          // be countable down 83 rows without running the mouse over them.
          selected
            ? "bg-blue-50 hover:bg-blue-50"
            : showSettled
              ? "cursor-pointer bg-emerald-50 hover:bg-emerald-100"
              : "cursor-pointer hover:bg-gray-50",
        )}
      >
        <span
          className="h-3 w-3 shrink-0 rounded-full ring-1 ring-inset ring-black/20"
          style={{ backgroundColor: swatch }}
          title={t("smartSurveyor.cablesBeta.routeColour")}
        />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          {/* line 1 — the identifier, and whatever is live on this run right now */}
          <span className="flex min-w-0 items-center gap-1.5">
            {hasKeyColumn &&
              (leadId ? (
                <span
                  title={leadId}
                  className={clsx(
                    "min-w-[2.75rem] max-w-[8rem] shrink-0 truncate text-[15px] font-semibold leading-tight tabular-nums",
                    selected ? "text-blue-700" : "text-slate-900",
                  )}
                >
                  {leadId}
                </span>
              ) : (
                // A dash in the column every other row prints an id in: he can
                // see there is none without being shouted at about it.
                <span
                  role="img"
                  aria-label={noPrintedIdName}
                  title={noPrintedIdName}
                  className="min-w-[2.75rem] shrink-0 text-[15px] leading-tight tabular-nums text-slate-500"
                >
                  —
                </span>
              ))}
            {showName && (
              <span
                title={item.text}
                className={clsx(
                  "truncate leading-tight",
                  nameLeads
                    ? clsx(
                        "text-[15px] font-semibold tabular-nums",
                        selected ? "text-blue-700" : "text-slate-900",
                      )
                    : "text-[13px] text-slate-700",
                )}
              >
                {item.text}
              </span>
            )}
            {deviceCount > 0 && (
              <span
                role="img"
                data-testid={isLumped ? "run-lumped" : undefined}
                aria-label={
                  isLumped
                    ? t("smartSurveyor.cablesBeta.runLumped", {
                        count: deviceCount,
                        limit: lumpedLimit,
                      })
                    : t("smartSurveyor.cablesBeta.runDeviceCount", {
                        count: deviceCount,
                      })
                }
                title={
                  isLumped
                    ? t("smartSurveyor.cablesBeta.runLumped", {
                        count: deviceCount,
                        limit: lumpedLimit,
                      })
                    : t("smartSurveyor.cablesBeta.runDeviceCount", {
                        count: deviceCount,
                      })
                }
                className={clsx(
                  "flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 text-[13px] font-medium leading-none tabular-nums",
                  isLumped
                    ? "bg-amber-100 text-amber-900"
                    : "bg-slate-100 text-slate-600",
                )}
              >
                <Plug
                  size={11}
                  aria-hidden
                  className={clsx(
                    "flex-none",
                    isLumped ? "text-amber-700" : "text-slate-500",
                  )}
                />
                {deviceCount}
              </span>
            )}
            {special && (
              // The system is named in words by the section header above this
              // group and by the card's accent border; on the row it only has to
              // be recognisable, so the word rides along as the accessible name.
              <span
                role="img"
                aria-label={sysLabel}
                title={sysLabel}
                className={clsx("inline-flex shrink-0", sys.text)}
              >
                {SysIcon ? (
                  <SysIcon size={14} />
                ) : (
                  <span className={clsx("h-2.5 w-2.5 rounded-full", sys.dot)} />
                )}
              </span>
            )}
            {isOrphaned && (
              <span className="shrink-0 rounded bg-slate-200 px-1.5 py-0.5 text-[13px] font-medium text-slate-700">
                {t("smartSurveyor.cablesBeta.circuitNotFed")}
              </span>
            )}
            {isPickingRun && selectId ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onPickRun?.(selectId);
                }}
                className="flex flex-none items-center gap-1 whitespace-nowrap rounded-md bg-blue-600 px-2 py-1 text-[13px] font-semibold text-white hover:bg-blue-700"
              >
                <Plus size={13} />
                {t("smartSurveyor.cablesBeta.addToThisRun")}
              </button>
            ) : underReview ? (
              <UnderReviewMark />
            ) : null}
          </span>
          {/* line 2 — what the run is, and what he already settled about it */}
          {(breakerBelow || kurztext || showSettled || share) && (
            <span className="flex min-w-0 items-center gap-2 text-[13px] leading-tight">
              {breakerBelow && (
                <span className="flex flex-none items-center gap-1.5">
                  <span
                    title={t("smartSurveyor.cablesBeta.rowBreakerId", {
                      key: breakerBelow,
                    })}
                    className="font-medium tabular-nums text-slate-700"
                  >
                    {breakerBelow}
                  </span>
                  {kurztext && (
                    <span aria-hidden className="text-slate-400">
                      ·
                    </span>
                  )}
                </span>
              )}
              {kurztext && (
                <span
                  title={kurztext}
                  className="min-w-0 truncate text-slate-600"
                >
                  {kurztext}
                </span>
              )}
              {showSettled && <SettledMark status={status} />}
              {holes > 0 && (
                // On the same line as the tray share, in the wall's own colour
                // rather than a warning colour: a Durchbruch is work he prices,
                // not a mistake he made.
                <span
                  data-testid="run-wall-crossings"
                  className="flex flex-none items-center gap-1 text-stone-700"
                >
                  <span
                    aria-hidden
                    className="h-2 w-3.5 flex-none rounded-sm bg-stone-700"
                  />
                  <span className="tabular-nums">
                    {t("smartSurveyor.cablesBeta.wallCrossings", {
                      count: holes,
                    })}
                  </span>
                </span>
              )}
              {share && (
                // On the line that already exists, after a kurztext that
                // already truncates: no extra height, and the metres block is
                // untouched. Same grey as the tray section, so a Bodenkanal
                // there and a run riding it here read as one subject.
                <span
                  title={t("smartSurveyor.cablesBeta.trayShareDetail", {
                    metres: num(round1(share.onTrayM)),
                    total: num(round1(share.routedM)),
                  })}
                  className="flex flex-none items-center gap-1 text-slate-600"
                >
                  <span
                    aria-hidden
                    className="h-1.5 w-5 flex-none overflow-hidden rounded-full bg-slate-200"
                  >
                    <span
                      className="block h-full rounded-full bg-slate-400"
                      style={{ width: `${share.percent}%` }}
                    />
                  </span>
                  <span className="tabular-nums">
                    {t("smartSurveyor.cablesBeta.trayShare", {
                      percent: share.percent,
                    })}
                  </span>
                </span>
              )}
            </span>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          <span className="whitespace-nowrap text-right">
            <span className="text-[17px] font-bold tabular-nums text-slate-900">
              {num(qty)}
            </span>
            <span className="ml-1 text-[13px] text-slate-600">{item.unit}</span>
          </span>
          {open ? (
            <ChevronDown size={16} className="text-slate-500" />
          ) : (
            <ChevronRight size={16} className="text-slate-500" />
          )}
        </span>
      </div>

      {/* expanded — length breakdown + destinations */}
      {open &&
        placeDetails(
          <div className="border-t border-gray-100 px-2.5 py-2.5 text-[13px]">
            {/* The way into the work, first — not last, under twelve device rows
              he has to scroll past. It was a small blue link stacked with two
              others: "ask karl more visible and eye catching". */}
            {/* Reshaping lives on Karl's card and nowhere else — "i wanted edit
              shape to be only in Karls card". This button is the way to it:
              it opens this run's card, where the Reshape tile, the hint and the
              way out all stand together. */}
            {circuitId && onAskKarlAbout && (
              <div className="mb-2.5 flex flex-col gap-1.5">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAskKarlAbout(circuitId);
                  }}
                  className="flex min-h-[40px] w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-[15px] font-semibold text-white shadow-sm hover:bg-blue-700"
                >
                  <MessageCircle size={16} className="flex-none" />
                  {t("smartSurveyor.cablesBeta.askKarlAboutRoute")}
                </button>
              </div>
            )}

            {diagram && (
              <div className="mb-2.5">
                <DiagramPageLink
                  diagram={diagram}
                  designators={[circuitBreaker, circuitKey, item.text]
                    .map((name) => name?.trim() ?? "")
                    .filter(
                      (name, index, all) => name && all.indexOf(name) === index,
                    )}
                  label={leadId || item.text}
                />
              </div>
            )}

            {canEditFacts &&
              circuitId &&
              deviceCount > 1 &&
              (onSplit || onAutoSplit) && (
                <div
                  className="mb-2.5 flex flex-col gap-1.5"
                  data-testid="circuit-split"
                >
                  <div className="flex gap-1.5">
                    {onSplit && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSplit(circuitId);
                        }}
                        className="flex min-h-[36px] flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2 text-[13.5px] font-medium text-slate-800 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700"
                      >
                        <Scissors size={14} className="flex-none" />
                        {t("smartSurveyor.cablesBeta.circuitSplit")}
                      </button>
                    )}
                    {onAutoSplit && (
                      <button
                        type="button"
                        aria-expanded={autoSplit.phase === "open"}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAutoSplit((current) =>
                            current.phase === "open"
                              ? { phase: "closed" }
                              : {
                                  phase: "open",
                                  size: String(
                                    (categoryOfThis &&
                                      DEFAULT_SPLIT_SIZE[categoryOfThis]) ||
                                      FALLBACK_SPLIT_SIZE,
                                  ),
                                  switches: "unanswered",
                                },
                          );
                        }}
                        className="flex min-h-[36px] flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2 text-[13.5px] font-medium text-slate-800 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700"
                      >
                        <Wand2 size={14} className="flex-none" />
                        {t("smartSurveyor.cablesBeta.circuitAutoSplit")}
                      </button>
                    )}
                  </div>
                  {onAutoSplit && autoSplit.phase === "open" && (
                    <div
                      className="flex flex-wrap items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50/60 px-2.5 py-2 text-[13px] text-slate-700"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span>
                        {t("smartSurveyor.cablesBeta.circuitAutoSplitAtMost")}
                      </span>
                      <input
                        type="number"
                        min={1}
                        value={autoSplit.size}
                        aria-label={t(
                          "smartSurveyor.cablesBeta.circuitAutoSplitAtMost",
                        )}
                        onChange={(event) => {
                          const size = event.target.value;
                          setAutoSplit((current) =>
                            current.phase === "open"
                              ? { ...current, size }
                              : current,
                          );
                        }}
                        className="h-8 w-16 rounded border border-slate-300 px-1.5 text-right tabular-nums"
                      />
                      <span>
                        {t(
                          "smartSurveyor.cablesBeta.circuitAutoSplitPerCircuit",
                        )}
                      </span>
                      {autoSplitPlan && (
                        <AutoSplitPreview
                          plan={autoSplitPlan}
                          switchChoice={autoSplit.switches}
                          onSwitchChoice={(switches) =>
                            setAutoSplit((current) =>
                              current.phase === "open"
                                ? { ...current, switches }
                                : current,
                            )
                          }
                          onTake={() => {
                            onAutoSplit(circuitId, autoSplitPlan);
                            setAutoSplit({ phase: "closed" });
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}

            {canEditFacts && (
              <div className="mb-2 grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1.5">
                {edit?.setCircuitKey && circuitId && (
                  <>
                    <span className="text-slate-500">
                      {t("smartSurveyor.cablesBeta.factCircuitKey")}
                    </span>
                    <span className="text-right">
                      <CircuitTextField
                        fieldLabel={t(
                          "smartSurveyor.cablesBeta.factCircuitKey",
                        )}
                        value={circuitKey ?? ""}
                        overridden={Boolean(
                          edit.circuitKeyOverrides?.[circuitId],
                        )}
                        onCommit={(key) => edit.setCircuitKey(circuitId, key)}
                        isTaken={isKeyTaken}
                      />
                    </span>
                  </>
                )}
                {edit?.setCircuitName && (
                  <>
                    <span className="text-slate-500">
                      {t("smartSurveyor.cablesBeta.factName")}
                    </span>
                    <span className="text-right">
                      <CircuitTextField
                        fieldLabel={t("smartSurveyor.cablesBeta.factName")}
                        value={item.text}
                        overridden={Boolean(edit.circuitNames?.[circuitId!])}
                        onCommit={(name) =>
                          edit.setCircuitName(circuitId!, name)
                        }
                      />
                    </span>
                  </>
                )}
                <span className="text-slate-500">
                  {t("smartSurveyor.cablesBeta.factSource")}
                </span>
                <span className="text-right">
                  <FeedPointField
                    value={feedPoint}
                    options={feedPointOptions}
                    overridden={feedPointOverridden}
                    onPick={(label) => {
                      if (label !== feedPoint) trackRunEdited("feedPoint");
                      edit!.setFeedPoint(circuitId!, label);
                    }}
                    onReset={() => {
                      trackRunEdited("feedPoint");
                      edit!.resetFeedPoint(circuitId!);
                    }}
                  />
                </span>

                <span className="text-slate-500">
                  {t("smartSurveyor.cablesBeta.factCableType")}
                </span>
                <span className="text-right">
                  <CableTypeField
                    value={cableType}
                    options={cableTypeOptions}
                    overridden={cableTypeOverridden}
                    onPick={(type) => {
                      trackRunEdited("cableType");
                      edit!.setCableType(circuitId!, type);
                    }}
                    onCreate={(type) => {
                      trackRunEdited("cableType");
                      edit!.addCableType(type, circuitId!);
                    }}
                    onReset={() => {
                      trackRunEdited("cableType");
                      edit!.resetCableType(circuitId!);
                    }}
                  />
                </span>
              </div>
            )}

            {wiringBasis && (
              <WiringBasis
                basis={wiringBasis}
                boardName={heightBoardName}
                legs={
                  canEditFacts && legMetres
                    ? {
                        metres: legMetres,
                        circuitType: cableType,
                        legTypes,
                        options: cableTypeOptions,
                        onPick: (kind, type) =>
                          edit!.setLegCableType(circuitId!, kind, type),
                        onCreate: (kind, type) =>
                          edit!.addCableType(type, circuitId!, kind),
                        onKeep: (kind) =>
                          edit!.setLegCableType(circuitId!, kind, cableType),
                        formatMetres: (value) => num(round1(value)),
                      }
                    : undefined
                }
              />
            )}

            {canEditLength ? (
              <div className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1.5">
                <span className="text-slate-500">
                  {t("smartSurveyor.cablesBeta.horizontalRouted")}
                </span>
                <span className="text-right font-medium tabular-nums text-slate-600">
                  {num(round1(billed))} m
                </span>

                <span className="text-slate-500">
                  {t("smartSurveyor.cablesBeta.verticalHeights", {
                    count: vertical?.changes ?? 0,
                  })}
                </span>
                <span className="text-right font-medium tabular-nums text-slate-600">
                  {num(round1(vertical?.verticalM ?? 0))} m
                </span>

                <span className="border-t border-slate-100 pt-1.5 font-semibold text-slate-700">
                  {t("smartSurveyor.cablesBeta.total")}
                </span>
                <span className="border-t border-slate-100 pt-1.5 text-right font-bold tabular-nums text-emerald-600">
                  {num(round1(total))} m
                </span>
                {typeShares.length > 1 &&
                  typeShares.map((share) => (
                    <Fragment key={share.cableType}>
                      <span className="text-slate-500">
                        {t("smartSurveyor.cablesBeta.cableTypesOnRun", {
                          type: share.cableType,
                        })}
                      </span>
                      <span className="text-right tabular-nums text-slate-600">
                        {num(round1(share.totalM))} m
                      </span>
                    </Fragment>
                  ))}
              </div>
            ) : (
              <div className="text-slate-500">
                {item.given_length_each_m !== null &&
                item.given_length_each_m !== undefined
                  ? `${item.device_count ?? 0} × ${item.given_length_each_m} m`
                  : item.cable_type ||
                    (item.device_count
                      ? t("smartSurveyor.cablesBeta.devicesCount", {
                          count: item.device_count,
                        })
                      : "—")}
                {item.method ? (
                  <span className="text-slate-600"> · {item.method}</span>
                ) : null}
              </div>
            )}

            {(destinations.length > 0 || removedForCircuit.length > 0) && (
              <div className="mt-2 space-y-1">
                <div className="text-[13px] font-medium text-slate-600">
                  {t("smartSurveyor.cablesBeta.destinationsWithCount", {
                    count: destinations.length,
                  })}
                </div>
                <DestinationList
                  entries={destinationEntries}
                  currentCircuitId={circuitId}
                  options={circuitOptions}
                  edit={edit}
                  onHighlight={onHighlightDestination}
                  heightProfiles={
                    circuitId
                      ? edit?.heightProfilesByCircuit[circuitId]
                      : undefined
                  }
                  boardName={heightBoardName}
                  onShowHeightMarks={onShowHeightMarks}
                  onHoverCircuit={onHover}
                />
                {removedForCircuit.map((m) => (
                  <div
                    key={m.destinationId}
                    className="flex items-center gap-1.5 text-[13px] text-slate-600"
                  >
                    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-dashed border-slate-300 bg-slate-50">
                      <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                    </span>
                    <span className="truncate line-through">
                      {t("smartSurveyor.cablesBeta.deviceNumbered", {
                        number: parseDestIndex(m.destinationId) + 1,
                      })}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        trackEvent(
                          ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_RUN_DEVICES_CHANGED,
                          { action: "restored", deviceCount: 1 },
                        );
                        edit?.restoreDevice(m.destinationId);
                      }}
                      className="ml-auto flex shrink-0 items-center gap-1 text-[13px] font-medium text-blue-700 hover:underline"
                    >
                      <RotateCcw size={13} />{" "}
                      {t("smartSurveyor.cablesBeta.restore")}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* So a run of his own reading 0 m reads as deliberate rather than as
              a defect — the same sentence a board he placed himself carries. */}
            {isCreatedByYou && circuitId && (
              <div className="mt-2 border-t border-slate-100 pt-2">
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 text-[13px] text-blue-700">
                    {t("smartSurveyor.cablesBeta.runCreatedByYou")}
                  </span>
                  {onRemoveCircuit && (
                    <button
                      type="button"
                      aria-expanded={isDeleting}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Nothing on it, nothing to strand: the question below
                        // only exists because the devices have to go somewhere.
                        if (deviceCount === 0) {
                          trackRunDeleted("none");
                          onRemoveCircuit(circuitId, null);
                          return;
                        }
                        setIsDeleting((open) => !open);
                      }}
                      title={t("smartSurveyor.cablesBeta.runRemove")}
                      aria-label={t("smartSurveyor.cablesBeta.runRemove")}
                      className={clsx(
                        "grid h-9 w-9 flex-none place-items-center rounded-md border",
                        isDeleting
                          ? "border-red-300 bg-red-50 text-red-600"
                          : "border-transparent text-slate-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600",
                      )}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>

                {/* The consequence BEFORE he commits, the same rule striking a
                  board follows: a run deleted under eleven devices leaves them
                  on a circuit that is not there — drawn nowhere, counted
                  nowhere, and nothing on screen saying where they went. */}
                {isDeleting && onRemoveCircuit && (
                  <div className="mt-1.5 rounded-md border border-red-200 bg-red-50 p-2">
                    <p className="text-[13px] text-slate-800">
                      {t("smartSurveyor.cablesBeta.runRemoveConsequence", {
                        count: deviceCount,
                      })}
                    </p>
                    <div className="mt-1.5">
                      <PanelSelect
                        value=""
                        options={circuitOptions
                          .filter((option) => option.id !== circuitId)
                          .map((option) => ({
                            value: option.id,
                            label: option.label,
                            color: option.color,
                          }))}
                        placeholder={t(
                          "smartSurveyor.cablesBeta.runRemoveMoveThemTo",
                        )}
                        ariaLabel={t(
                          "smartSurveyor.cablesBeta.runRemoveMoveThemTo",
                        )}
                        onPick={(target) => {
                          setIsDeleting(false);
                          trackRunDeleted("movedToRun");
                          onRemoveCircuit(circuitId, target);
                        }}
                      />
                    </div>
                    <div className="mt-1.5 flex items-center gap-2">
                      {/* Back to the estimation's own answer, which for a device
                        it never placed is no circuit at all — where it was
                        before he adopted it, and still on the sheet. */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsDeleting(false);
                          trackRunDeleted("restored");
                          onRemoveCircuit(circuitId, null);
                        }}
                        className="flex h-8 items-center rounded-md border border-red-200 bg-white px-2 text-[13px] font-medium text-red-700 hover:bg-red-50"
                      >
                        {t("smartSurveyor.cablesBeta.runRemovePutThemBack")}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsDeleting(false);
                        }}
                        className="flex h-8 items-center px-1 text-[13px] font-medium text-slate-600 hover:text-slate-900"
                      >
                        {t("smartSurveyor.cablesBeta.cancel")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>,
        )}
    </li>
  );
}
